import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Link2, Plus, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const DEP_TYPES = [
  { key: "FS", label: "Finish → Start (default)" },
  { key: "SS", label: "Start → Start" },
  { key: "FF", label: "Finish → Finish" },
  { key: "SF", label: "Start → Finish" },
];

// ── Simple left-to-right DAG layout ──────────────────────────────────────────
const NODE_W = 140;
const NODE_H = 36;
const H_GAP = 80;
const V_GAP = 20;

function layoutGraph(tasks, deps) {
  if (!deps.length || !tasks.length) return { nodes: [], edges: [] };
  const taskSet = new Set(tasks.map((t) => t.task_id));
  const inEdges = {};
  const outEdges = {};
  for (const d of deps) {
    if (!taskSet.has(d.predecessor_id) || !taskSet.has(d.successor_id)) continue;
    outEdges[d.predecessor_id] = outEdges[d.predecessor_id] || [];
    outEdges[d.predecessor_id].push(d.successor_id);
    inEdges[d.successor_id] = inEdges[d.successor_id] || [];
    inEdges[d.successor_id].push(d.predecessor_id);
  }

  // Only include nodes that appear in at least one dep
  const involved = new Set([...deps.map((d) => d.predecessor_id), ...deps.map((d) => d.successor_id)]);
  const relevantTasks = tasks.filter((t) => involved.has(t.task_id));

  // Topological rank (column)
  const rank = {};
  const assignRank = (id, r) => {
    if (rank[id] != null && rank[id] >= r) return;
    rank[id] = r;
    for (const next of (outEdges[id] || [])) assignRank(next, r + 1);
  };
  for (const t of relevantTasks) {
    if (!inEdges[t.task_id]) assignRank(t.task_id, 0);
  }
  // fallback for any unranked
  for (const t of relevantTasks) {
    if (rank[t.task_id] == null) rank[t.task_id] = 0;
  }

  // Group by column, assign row within column
  const cols = {};
  for (const t of relevantTasks) {
    const col = rank[t.task_id];
    cols[col] = cols[col] || [];
    cols[col].push(t);
  }

  const nodes = {};
  const maxCol = Math.max(...Object.keys(cols).map(Number));
  for (let col = 0; col <= maxCol; col++) {
    (cols[col] || []).forEach((t, row) => {
      nodes[t.task_id] = {
        id: t.task_id,
        title: t.title,
        x: col * (NODE_W + H_GAP),
        y: row * (NODE_H + V_GAP),
        col,
      };
    });
  }

  const svgW = (maxCol + 1) * (NODE_W + H_GAP) + 20;
  const svgH = Math.max(...Object.values(nodes).map((n) => n.y)) + NODE_H + 20;

  const edges = deps
    .filter((d) => nodes[d.predecessor_id] && nodes[d.successor_id])
    .map((d) => {
      const from = nodes[d.predecessor_id];
      const to = nodes[d.successor_id];
      const x1 = from.x + NODE_W;
      const y1 = from.y + NODE_H / 2;
      const x2 = to.x;
      const y2 = to.y + NODE_H / 2;
      const mx = (x1 + x2) / 2;
      return { d: `M ${x1} ${y1} C ${mx} ${y1} ${mx} ${y2} ${x2} ${y2}`, type: d.dependency_type, lag: d.lag_days };
    });

  return { nodes: Object.values(nodes), edges, svgW, svgH };
}

// ── Visual graph component ────────────────────────────────────────────────────
const DepGraph = ({ tasks, deps }) => {
  const { nodes, edges, svgW, svgH } = useMemo(() => layoutGraph(tasks, deps), [tasks, deps]);
  if (!nodes.length) return null;

  return (
    <div className="border border-[#1F2937] bg-[#0D1117] p-5 overflow-x-auto">
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#6B7280] mb-4">Dependency graph</div>
      <svg width={svgW} height={svgH + 20} className="min-w-full">
        <defs>
          <marker id="arrow" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
            <path d="M0,0 L0,6 L8,3 z" fill="#002FA7" />
          </marker>
        </defs>
        {edges.map((e, i) => (
          <path
            key={i}
            d={e.d}
            fill="none"
            stroke="#002FA7"
            strokeWidth={1.5}
            strokeOpacity={0.8}
            markerEnd="url(#arrow)"
          />
        ))}
        {nodes.map((n) => (
          <g key={n.id} transform={`translate(${n.x},${n.y})`}>
            <rect width={NODE_W} height={NODE_H} fill="#111827" stroke="#374151" strokeWidth={1} rx={2} />
            <text
              x={NODE_W / 2}
              y={NODE_H / 2 + 4}
              textAnchor="middle"
              fontSize={10}
              fill="#E5E7EB"
              style={{ userSelect: "none" }}
            >
              {n.title.length > 18 ? n.title.slice(0, 17) + "…" : n.title}
            </text>
          </g>
        ))}
      </svg>
    </div>
  );
};

const Dependencies = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [deps, setDeps] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ predecessor_id: "", successor_id: "", dependency_type: "FS", lag_days: 0 });

  const load = async () => {
    const [p, t, d] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/tasks?project_id=${projectId}`),
      api.get(`/dependencies?project_id=${projectId}`),
    ]);
    setProject(p.data);
    setTasks(t.data);
    setDeps(d.data);
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  const taskById = useMemo(() => {
    const m = {};
    for (const t of tasks) m[t.task_id] = t;
    return m;
  }, [tasks]);

  // Memoise the dependency-map rollup to avoid the O(n*m) recompute on every keystroke.
  const dependencyMap = useMemo(() => {
    if (!tasks.length || !deps.length) return [];
    return tasks
      .filter((t) => deps.some((d) => d.predecessor_id === t.task_id || d.successor_id === t.task_id))
      .map((t) => ({
        task: t,
        incoming: deps.filter((d) => d.successor_id === t.task_id),
        outgoing: deps.filter((d) => d.predecessor_id === t.task_id),
      }));
  }, [tasks, deps]);

  const create = async () => {
    if (!form.predecessor_id || !form.successor_id) return;
    try {
      const { data } = await api.post("/dependencies", { ...form, lag_days: parseInt(form.lag_days, 10) || 0 });
      setDeps([data, ...deps]);
      setOpen(false);
      setForm({ predecessor_id: "", successor_id: "", dependency_type: "FS", lag_days: 0 });
      toast.success("Dependency added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add dependency");
    }
  };

  const remove = async (id) => {
    await api.delete(`/dependencies/${id}`);
    setDeps(deps.filter((d) => d.dependency_id !== id));
    toast.success("Dependency removed");
  };

  return (
    <div>
      <PageHeader
        backTo={`/workspace/${projectId}`}
        eyebrow={project ? `Dependencies · ${project.name}` : "Dependencies"}
        title="Task Dependencies"
        description="Predecessor → successor links with type and lag. Cycle-protected and project-scoped."
        actions={
          <div className="flex gap-2">
            <Link to={`/workspace/${projectId}`}>
              <Button variant="outline" className="rounded-none border-gray-900" data-testid="back-to-project-from-deps">
                <ArrowLeft className="w-4 h-4 mr-1" /> Project
              </Button>
            </Link>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="new-dep-btn">
                  <Plus className="w-4 h-4 mr-1" /> New dependency
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none border-gray-900 sm:max-w-lg">
                <DialogHeader><DialogTitle className="font-display tracking-tight">New dependency</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Predecessor</Label>
                    <Select value={form.predecessor_id} onValueChange={(v) => setForm({ ...form, predecessor_id: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="dep-pre-select"><SelectValue placeholder="Select predecessor task" /></SelectTrigger>
                      <SelectContent>
                        {tasks.map((t) => <SelectItem key={t.task_id} value={t.task_id}>[{t.item_type}] {t.title}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-center"><ArrowRight className="w-5 h-5 text-gray-400" /></div>
                  <div>
                    <Label className="text-[11px] uppercase tracking-[0.2em]">Successor</Label>
                    <Select value={form.successor_id} onValueChange={(v) => setForm({ ...form, successor_id: v })}>
                      <SelectTrigger className="rounded-none mt-1" data-testid="dep-suc-select"><SelectValue placeholder="Select successor task" /></SelectTrigger>
                      <SelectContent>
                        {tasks.filter((t) => t.task_id !== form.predecessor_id).map((t) => (
                          <SelectItem key={t.task_id} value={t.task_id}>[{t.item_type}] {t.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Type</Label>
                      <Select value={form.dependency_type} onValueChange={(v) => setForm({ ...form, dependency_type: v })}>
                        <SelectTrigger className="rounded-none mt-1" data-testid="dep-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{DEP_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-[0.2em]">Lag (days)</Label>
                      <Input type="number" className="rounded-none mt-1" data-testid="dep-lag-input" value={form.lag_days} onChange={(e) => setForm({ ...form, lag_days: e.target.value })} />
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={create} disabled={!form.predecessor_id || !form.successor_id} className="rounded-none bg-[#002FA7] hover:bg-[#002480]" data-testid="confirm-create-dep">Add dependency</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="px-5 sm:px-8 lg:px-12 py-8 space-y-6">
        {deps.length > 0 && <DepGraph tasks={tasks} deps={deps} />}
        {deps.length === 0 ? (
          <div className="border border-dashed border-[#374151] p-16 text-center">
            <Link2 className="w-8 h-8 text-[#002FA7] mx-auto" strokeWidth={1.5} />
            <h3 className="font-display font-black text-2xl tracking-tight mt-4">No dependencies yet</h3>
            <p className="text-gray-500 mt-2">Link tasks to model "must finish before" relationships. Useful for Gantt and critical path.</p>
            <Button onClick={() => setOpen(true)} className="rounded-none mt-6 bg-[#002FA7] hover:bg-[#002480]" data-testid="empty-new-dep-btn">
              <Plus className="w-4 h-4 mr-1" /> New dependency
            </Button>
          </div>
        ) : (
          <div className="border border-[#1F2937]">
            <div className="px-6 py-3 border-b border-[#1F2937] text-xs uppercase tracking-[0.25em] text-gray-500 flex items-center justify-between">
              <span>{deps.length} {deps.length === 1 ? "Dependency" : "Dependencies"}</span>
              <span className="font-mono text-gray-400">Predecessor → Successor</span>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-[#030712] text-[10px] uppercase tracking-[0.2em] text-slate-400">
                <tr>
                  <th className="text-left p-3 font-semibold">Predecessor</th>
                  <th className="text-left p-3 font-semibold w-12"></th>
                  <th className="text-left p-3 font-semibold">Successor</th>
                  <th className="text-left p-3 font-semibold">Type</th>
                  <th className="text-left p-3 font-semibold">Lag</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {deps.map((d) => (
                  <tr key={d.dependency_id} className="border-t border-[#1F2937] hover:bg-[#030712]" data-testid={`dep-row-${d.dependency_id}`}>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] uppercase tracking-[0.2em] bg-[#111827] text-slate-300 px-1.5 py-0.5">{d.predecessor?.item_type}</span>
                        <span className="font-medium">{d.predecessor?.title || "—"}</span>
                      </div>
                    </td>
                    <td className="p-3"><ArrowRight className="w-4 h-4 text-[#002FA7]" /></td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] uppercase tracking-[0.2em] bg-[#111827] text-slate-300 px-1.5 py-0.5">{d.successor?.item_type}</span>
                        <span className="font-medium">{d.successor?.title || "—"}</span>
                      </div>
                    </td>
                    <td className="p-3 font-mono text-xs">{d.dependency_type}</td>
                    <td className="p-3 font-mono text-xs">{d.lag_days > 0 ? `+${d.lag_days}d` : d.lag_days < 0 ? `${d.lag_days}d` : "—"}</td>
                    <td className="p-3 text-right">
                      <button onClick={() => remove(d.dependency_id)} className="text-gray-400 hover:text-red-600 p-2 -m-1 inline-flex items-center justify-center" data-testid={`delete-dep-${d.dependency_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Per-task summary */}
        {dependencyMap.length > 0 && (
          <div className="border border-[#1F2937]">
            <div className="px-6 py-3 border-b border-[#1F2937] text-xs uppercase tracking-[0.25em] text-gray-500">Dependency map</div>
            <div className="divide-y divide-gray-100">
              {dependencyMap.map(({ task: t, incoming, outgoing }) => (
                <div key={t.task_id} className="px-6 py-3 grid grid-cols-12 gap-3 items-center text-sm">
                  <div className="col-span-4 font-medium">{t.title}</div>
                  <div className="col-span-4 text-xs text-gray-500">
                    {incoming.length > 0 && (
                      <div><span className="font-mono text-red-600">↓ blocked by:</span> {incoming.map((d) => taskById[d.predecessor_id]?.title).filter(Boolean).join(", ")}</div>
                    )}
                  </div>
                  <div className="col-span-4 text-xs text-gray-500">
                    {outgoing.length > 0 && (
                      <div><span className="font-mono text-emerald-700">↑ blocks:</span> {outgoing.map((d) => taskById[d.successor_id]?.title).filter(Boolean).join(", ")}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dependencies;
